import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../hooks/useAuth';
import { notificationService } from '../../services/loanService';
import { useTheme } from '../../context/ThemeContext';
import logo from '../../assets/logo.png';

function ThemeToggle() {
  const { theme, toggleTheme } = useTheme();
  return (
    <button
      onClick={toggleTheme}
      title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
      style={{
        background: 'var(--subtle-bg)',
        border: '1px solid var(--border-bright)',
        borderRadius: '10px',
        padding: '0.5rem 0.625rem',
        color: 'var(--text-2)',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        fontSize: '1rem',
        lineHeight: 1,
        transition: 'all .15s',
      }}
      onMouseEnter={e => { e.currentTarget.style.background = 'var(--subtle-bg-hover)'; e.currentTarget.style.color = 'var(--text-1)'; }}
      onMouseLeave={e => { e.currentTarget.style.background = 'var(--subtle-bg)'; e.currentTarget.style.color = 'var(--text-2)'; }}
    >
      {theme === 'dark' ? '☀' : '🌙'}
    </button>
  );
}

function TierBadge({ tier }) {
  if (!tier) return null;
  return (
    <span className={`badge tier-${tier}`} style={{ fontSize: '0.65rem', padding: '0.2rem 0.5rem' }}>
      {tier.charAt(0).toUpperCase() + tier.slice(1)}
    </span>
  );
}

function NotificationBell() {
  const navigate = useNavigate();
  const { data } = useQuery({
    queryKey: ['notif-unread-count'],
    queryFn: () => notificationService.getAll({ is_read: false, page_size: 1 }).then(r => r.data.unread_count || 0),
    refetchInterval: 30000,
  });
  const count = data || 0;

  return (
    <button
      onClick={() => navigate('/notifications')}
      style={{
        position: 'relative', background: 'var(--subtle-bg)',
        border: '1px solid var(--border-bright)', borderRadius: '10px',
        padding: '0.5rem 0.625rem', color: 'var(--text-2)', cursor: 'pointer',
        display: 'flex', alignItems: 'center', transition: 'all .15s',
      }}
      onMouseEnter={e => { e.currentTarget.style.background = 'var(--subtle-bg-hover)'; e.currentTarget.style.color = 'var(--text-1)'; }}
      onMouseLeave={e => { e.currentTarget.style.background = 'var(--subtle-bg)'; e.currentTarget.style.color = 'var(--text-2)'; }}
      title="Notifications"
    >
      <span style={{ fontSize: '1rem', lineHeight: 1 }}>🔔</span>
      {count > 0 && (
        <span style={{
          position: 'absolute', top: -4, right: -4,
          width: 17, height: 17, borderRadius: '50%',
          background: '#FF4757', color: '#fff',
          fontSize: '0.6rem', fontWeight: 700,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          border: '2px solid var(--bg)',
          boxShadow: '0 0 8px rgba(255,71,87,0.6)',
        }}>
          {count > 9 ? '9+' : count}
        </span>
      )}
    </button>
  );
}

const REVIEWER_CHIP = {
  maker:   { label: 'MAKER',   color: '#00C896', bg: 'rgba(0,200,150,0.12)' },
  checker: { label: 'CHECKER', color: '#818CF8', bg: 'rgba(129,140,248,0.12)' },
  both:    { label: 'M+C',     color: '#F5A623', bg: 'rgba(245,166,35,0.12)' },
};

export default function Navbar({ onMenuClick }) {
  const { user, logout, isAdmin, reviewerType } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const reviewerChip = reviewerType ? REVIEWER_CHIP[reviewerType] : null;

  return (
    <nav className="navbar" style={{
      height: 60,
      background: 'var(--nav-bg)',
      backdropFilter: 'blur(20px)',
      borderBottom: '1px solid var(--border)',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '0 1.5rem',
      position: 'sticky', top: 0, zIndex: 100,
      gap: '0.75rem',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', minWidth: 0 }}>
        {user && (
          <button
            className="navbar-hamburger"
            onClick={onMenuClick}
            aria-label="Toggle menu"
            style={{
              alignItems: 'center', justifyContent: 'center',
              background: 'var(--subtle-bg)', border: '1px solid var(--border-bright)',
              borderRadius: '10px', width: 36, height: 36, flexShrink: 0,
              color: 'var(--text-1)', fontSize: '1.1rem', cursor: 'pointer',
            }}
          >
            ☰
          </button>
        )}
        {/* Logo */}
        <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: '0.625rem', textDecoration: 'none', minWidth: 0 }}>
        <img src={logo} alt="PCredit" style={{ height: 32, width: 'auto' }} />
        {isAdmin && (
          <span style={{
            fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.08em',
            color: '#f5a623', background: 'rgba(245,166,35,0.12)',
            border: '1px solid rgba(245,166,35,0.3)',
            padding: '0.15rem 0.5rem', borderRadius: '99px',
          }}>ADMIN</span>
        )}
        </Link>
      </div>

      {user && (
        <div className="navbar-actions" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <ThemeToggle />
          <NotificationBell />

          <div style={{ position: 'relative' }}>
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              style={{
                display: 'flex', alignItems: 'center', gap: '0.625rem',
                background: 'var(--subtle-bg)',
                border: '1px solid var(--border-bright)',
                borderRadius: '10px',
                padding: '0.375rem 0.75rem',
                color: 'var(--text-1)',
                transition: 'all .15s',
              }}
            >
              <div style={{
                width: 28, height: 28, borderRadius: '50%',
                background: 'linear-gradient(135deg, #1596E0, #0B6FB0)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '0.75rem', fontWeight: 700, color: '#fff', flexShrink: 0,
              }}>
                {user.first_name?.[0]?.toUpperCase() || '?'}
              </div>
              <div className="navbar-user-info" style={{ textAlign: 'left' }}>
                <div style={{ fontSize: '0.8125rem', fontWeight: 600, lineHeight: 1.2 }}>
                  {user.first_name} {user.last_name}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', marginTop: '2px' }}>
                  <TierBadge tier={user.tier} />
                  {reviewerChip && (
                    <span style={{
                      fontSize: '0.55rem', fontWeight: 800, padding: '0.1rem 0.4rem',
                      borderRadius: 99, letterSpacing: '0.04em',
                      background: reviewerChip.bg, color: reviewerChip.color,
                    }}>
                      {reviewerChip.label}
                    </span>
                  )}
                </div>
              </div>
              <span style={{ color: 'var(--text-3)', fontSize: '0.7rem', marginLeft: '0.25rem' }}>
                {menuOpen ? '▲' : '▼'}
              </span>
            </button>

            {menuOpen && (
              <div style={{
                position: 'absolute', right: 0, top: 'calc(100% + 8px)',
                background: 'var(--surface)',
                border: '1px solid var(--border-bright)',
                borderRadius: '12px',
                minWidth: '180px',
                boxShadow: 'var(--shadow-lg)',
                overflow: 'hidden',
                zIndex: 200,
              }}
                onMouseLeave={() => setMenuOpen(false)}
              >
                <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid var(--border)' }}>
                  <div style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--text-1)' }}>{user.email}</div>
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-3)', marginTop: '2px', textTransform: 'capitalize' }}>
                    {user.role} · {user.tier || 'basic'} tier
                  </div>
                </div>
                {[
                  { to: '/profile', label: 'Profile & KYC' },
                  { to: '/notifications', label: 'Notifications' },
                  { to: '/support', label: 'Support' },
                  { to: '/settings', label: 'Settings' },
                ].map(({ to, label }) => (
                  <Link key={to} to={to} onClick={() => setMenuOpen(false)}
                    style={{ display: 'block', padding: '0.65rem 1rem', fontSize: '0.875rem', color: 'var(--text-2)', transition: 'all .15s', textDecoration: 'none' }}
                    onMouseEnter={e => e.target.style.background = 'var(--surface-2)'}
                    onMouseLeave={e => e.target.style.background = 'transparent'}
                  >
                    {label}
                  </Link>
                ))}
                <div style={{ borderTop: '1px solid var(--border)' }}>
                  <button
                    onClick={() => { setMenuOpen(false); logout(); }}
                    style={{
                      display: 'block', width: '100%', textAlign: 'left',
                      padding: '0.65rem 1rem', fontSize: '0.875rem',
                      color: 'var(--danger)', background: 'transparent', border: 'none',
                      transition: 'all .15s', cursor: 'pointer',
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,71,87,0.08)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    Sign out
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}
